import { useTranslation } from '../i18n/useTranslation';
import { formatExternalFeedValue } from '../lib/marketIntelFeeds';
import type { ExternalFeed } from '../types/gateway';

interface ExternalFeedPanelProps {
  feeds: ExternalFeed[];
  className?: string;
}

function formatAge(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function ExternalFeedPanel({ feeds, className = '' }: ExternalFeedPanelProps) {
  const { t } = useTranslation();
  return (
    <div className={`bg-card rounded border border-border p-2 ${className}`}>
      <div className="drag-handle h-5 flex items-center cursor-grab active:cursor-grabbing mb-1">
        <span className="panel-header">{t('external.header')}</span>
        <span className="ml-auto text-[8px] font-mono text-muted">{feeds.length}</span>
      </div>
      {feeds.length === 0 ? (
        <div className="flex items-center justify-center h-[calc(100%-24px)]">
          <span className="text-[9px] font-mono text-muted">{t('external.empty')}</span>
        </div>
      ) : (
        <div className="overflow-auto h-[calc(100%-24px)]">
          <table className="w-full text-[9px] font-mono">
            <thead>
              <tr className="text-muted-foreground text-left uppercase">
                <th className="pb-1 pr-2">{t('external.source')}</th>
                <th className="pb-1 pr-2">{t('external.instrument')}</th>
                <th className="pb-1 pr-2">{t('external.value')}</th>
                <th className="pb-1 text-right">{t('external.updated')}</th>
              </tr>
            </thead>
            <tbody>
              {feeds.map((feed, i) => (
                <tr key={`${feed.source}-${feed.instrument ?? 'global'}-${i}`} className="border-t border-border-subtle">
                  <td className="py-0.5 pr-2 text-primary">{feed.source}</td>
                  <td className="py-0.5 pr-2 text-foreground">{feed.instrument ?? '—'}</td>
                  <td className="py-0.5 pr-2 text-foreground truncate max-w-[120px]">{formatExternalFeedValue(feed)}</td>
                  <td className="py-0.5 text-right text-muted">{formatAge(feed.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
